import { formatNumber, formatPercent } from '@angular/common';
import { CmpsCardRowData, RowType, TrendType } from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { KpiType } from '../../coaching-insight/insight-widget/model/kpi-type.model';
import { epicDependencies } from '../../store';
import { KpiTypeTranslationMap } from '../constants/kpi-type-translation-map';
import { CardItem } from '../models/card-item.model';
import { CardRowData } from '../models/card-row-data.model';
import { HeaderRowData } from '../models/header-row-data.model';
import { StagingKpiThreshold } from '../models/staging-kpi-threshold.model';
import { WpCoachingCard } from '../models/wp-coaching-card.model';
import { TranslationKeyMap } from '../my-coaching-card.i18n';

import { determineTrend } from './determine-trend.function';
import { getHeaderRowData } from './get-header-row-data.function';
import { getProgressDataWinningPct } from './get-progress-data-winning-pct.function';

function getWinningPctRowData(
    wpCoachingCard: WpCoachingCard,
    kpiTypeId: number,
): CmpsCardRowData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    const { trendId, trendColor } = determineTrend(
        wpCoachingCard?.winning,
        wpCoachingCard?.prevWinning,
    );

    return {
        dataRowNameLabel: translateService.instant(
            KpiTypeTranslationMap[kpiTypeId],
        ),
        rowType: getCardType(kpiTypeId),
        isVisible: true,
        percentageValue:
            wpCoachingCard?.winning != null
                ? formatPercent(wpCoachingCard?.winning, 'en-CA', '1.0-0')
                : '-',
        placeholderStr:
            trendId !== TrendType.UNKNOWN
                ? null
                : translateService.instant(TranslationKeyMap._9402_NA),
        opportunityColor: wpCoachingCard?.winningThresholdColour,
        menuTooltipData: [
            {
                label: translateService.instant(TranslationKeyMap._725_ACTUAL),
                value:
                    wpCoachingCard?.winning != null
                        ? formatNumber(
                              wpCoachingCard?.winning * 100,
                              'en-CA',
                              '1.0-2',
                          )
                        : '-',
            },
            {
                label: translateService.instant(TranslationKeyMap._726_TARGET),
                value:
                    wpCoachingCard?.winningTarget != null
                        ? formatNumber(
                              wpCoachingCard?.winningTarget * 100,
                              'en-CA',
                              '1.0-2',
                          )
                        : '-',
            },
            {
                label: translateService.instant(
                    TranslationKeyMap._768___TO_TARGET,
                ),
                value:
                    wpCoachingCard?.winningPercentToTarget != null
                        ? formatPercent(
                              wpCoachingCard?.winningPercentToTarget,
                              'en-CA',
                              '1.0-2',
                          )
                        : '-',
            },
            {
                label: translateService.instant(
                    TranslationKeyMap._4105_PREVIOUS_ACTUAL,
                ),
                value:
                    wpCoachingCard?.prevWinning != null
                        ? formatNumber(
                              wpCoachingCard?.prevWinning * 100,
                              'en-CA',
                              '1.0-2',
                          )
                        : '-',
            },
        ],
        trendId,
        trendColor,
    };
}

function getCardRowsData(
    card: WpCoachingCard,
    coachingCardThreshold: StagingKpiThreshold[],
    kpiTypeId?: number,
): CardRowData[] {
    const cardRows: CardRowData[] = [
        {
            rowData: getWinningPctRowData(card, kpiTypeId),
            progressData: getProgressDataWinningPct(
                card,
                coachingCardThreshold,
            ),
        },
    ];

    return cardRows;
}
export function getWinningPctCardItem(
    wpCoachingCard: WpCoachingCard | null,
    coachingCardThreshold: StagingKpiThreshold[],
    kpiTypeId?: number,
): CardItem {
    const { startDateDataRange, endDateDataRange } = wpCoachingCard ?? {};
    const headerData = getHeaderRowData(
        startDateDataRange,
        endDateDataRange,
        kpiTypeId,
    );
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);

    if (wpCoachingCard == null || Object.keys(wpCoachingCard).length === 0) {
        return createNoDataRow(headerData, translateService, kpiTypeId);
    }

    const cardRowsData = getCardRowsData(
        wpCoachingCard,
        coachingCardThreshold,
        kpiTypeId,
    );
    const cardType = getCardType(kpiTypeId);

    return {
        headerData,
        cardRowsData,
        type: cardType,
    };
}

function createNoDataRow(
    headerData: HeaderRowData,
    translateService: TranslateService,
    kpiTypeId: number,
): CardItem {
    const noDataLabelKey =
        kpiTypeId === KpiType.FL_WINNING_PERCENTAGE
            ? TranslationKeyMap._11166_NO_FRONT_LINE_WINNING__DATA_AVAILABLE
            : TranslationKeyMap._10102_NO_WINNING__DATA_AVAILABLE;

    return {
        headerData,
        cardRowsData: [
            {
                rowData: {
                    dataRowNameLabel: translateService.instant(
                        KpiTypeTranslationMap[kpiTypeId],
                    ),
                    rowType: getCardType(kpiTypeId),
                    isVisible: true,
                    noDataLabel: translateService.instant(noDataLabelKey),
                },
            },
        ],
        type: getCardType(kpiTypeId),
    };
}

function getCardType(kpiTypeId: number): RowType {
    return kpiTypeId === KpiType.FL_WINNING_PERCENTAGE
        ? RowType.FLWP
        : RowType.WP;
}
